import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { CommonMessage, useLocale } from '@frontend/lang';
import { ExtendedPudoTransaction } from '@frontend/transaction/types';
import { LuArrowRight, LuCalendar, LuStickyNote, LuUser } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

interface TransactionCardProps {
    isLoading?: boolean;
    isDisabled?: boolean;
    transaction: ExtendedPudoTransaction;
    onClick?: (transaction: ExtendedPudoTransaction) => void;
    showShippingNotes?: boolean;
}

export const TransactionCard = (props: TransactionCardProps) => {
    const { transaction, onClick, isLoading, isDisabled, showShippingNotes } = props;
    const { locale } = useLocale();

    const date = formatDateTimeWithLocale(new Date(transaction.creation_timestamp), locale, DateFormat.DD_MM_YYYY_HH_MM);
    const isClickable = !!onClick && !isDisabled;

    return (
        <button
            type='button'
            disabled={isDisabled || !onClick}
            className={`bg-background-surface relative inline-flex flex-col items-start justify-start gap-3 self-stretch rounded-xl p-4 text-left outline-2 -outline-offset-2 transition-colors ${
                isDisabled
                    ? 'outline-border-default cursor-not-allowed opacity-50'
                    : isClickable
                      ? 'outline-brand-primary hover:bg-brand-light cursor-pointer'
                      : 'outline-border-default'
            }`}
            onClick={() => isClickable && onClick(transaction)}>
            {isLoading && (
                <div className='absolute inset-0 flex items-center justify-center rounded-xl'>
                    <Spinner className='text-border-default fill-brand-primary size-8' />
                </div>
            )}
            <div className='inline-flex items-center justify-between self-stretch'>
                <div className='text-foreground-default justify-start text-base font-semibold'>{transaction.tracking_number}</div>
                <div
                    data-show-icon='false'
                    data-variant='Info'
                    className='bg-brand-muted flex items-start justify-center gap-1 rounded-full px-2 py-1'>
                    <div className='text-background-surface justify-start text-center text-sm font-semibold'>
                        {CommonMessage.OBJECTS.SLOT.DEFAULT} {transaction.slot?.name}
                    </div>
                </div>
            </div>
            <div className='flex flex-col items-start justify-start gap-1 self-stretch'>
                {showShippingNotes && transaction.shipping_notes && (
                    <div className='inline-flex items-start justify-start gap-2 self-stretch'>
                        <LuStickyNote className='mt-0.5 size-5 shrink-0' />
                        <div className='text-brand-muted min-w-0 justify-start text-base font-medium wrap-break-word'>
                            {CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES}: {transaction.shipping_notes}
                        </div>
                    </div>
                )}
                <div className='inline-flex items-center justify-start gap-2 self-stretch'>
                    <LuUser className='size-5' />
                    <div className='text-brand-muted justify-start text-base font-medium'>
                        {CommonMessage.OBJECTS.TRANSACTION.SENDER}: {transaction.senders?.[0]?.name}
                    </div>
                </div>
                <div className='inline-flex items-center justify-start gap-2 self-stretch'>
                    <LuUser className='size-5' />
                    <div className='text-brand-muted justify-start text-base font-medium'>
                        {CommonMessage.OBJECTS.TRANSACTION.RECEIVER}: {transaction.receivers?.[0]?.name}
                    </div>
                </div>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center gap-2'>
                        <LuCalendar className='size-5' />
                        <div className='text-brand-muted justify-start text-base font-medium'>
                            {CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT} {date}
                        </div>
                    </div>

                    {isClickable && (
                        <div className='text-brand-primary inline-flex items-center gap-1 text-sm font-semibold'>
                            <FormattedMessage
                                id='kiosk.wizard.transaction-card.select'
                                description='Call-to-action label on a selectable transaction card in the authenticated pick or drop method screen'
                                defaultMessage='Select'
                            />
                            <LuArrowRight className='size-5' />
                        </div>
                    )}
                </div>
            </div>
        </button>
    );
};
